<template>
  <v-app :style="styles">
      <DisplayWatcher />

    <v-system-bar
      v-if="safeArea.top"
      v-bind="systemBarProps"
      fixed
    />

    <v-overlay
      :model-value="!isOnline || userId === undefined || !routerReady"
      class="loader-overlay"
      scrim="neutral0"
      :opacity="1"
    >
      <div class="d-flex flex-column align-center justify-center full-height text-neutral900">
        <!-- Cold-launch share intent: show the "adding item" message for the
             whole boot instead of the generic app loader. AddBookmark/AddNote
             mount behind this overlay and their own busy overlay takes over
             seamlessly once auth resolves and this overlay hides. -->
        <IntentAddingMessage v-if="intent && isOnline" />
        <template v-else>
          <v-spacer />
          <v-responsive :aspect-ratio="92/28" max-width="164">
            <Logo class="svg-width text-neutral900" />
          </v-responsive>
          <v-spacer />

          <v-progress-circular v-if="isOnline" color="blue50" indeterminate size="48" />
          <template v-else>
            <span v-text="t('common.NO_NETWORK')" />
            <BrandButton class="mt-3" @click="onNetworkRetry">
              {{ t('cta.try_again') }}
            </BrandButton>
          </template>
          <v-spacer />
        </template>
      </div>
    </v-overlay>

    <UpdateApplicationOverlay />

    <v-overlay
      :model-value="isUnderMaintenance"
      class="loader-overlay"
      scrim="neutral0"
      :opacity="1"
    >
      <div class="d-flex flex-column align-center justify-center full-height text-neutral900">
        <v-spacer />
        <v-responsive :aspect-ratio="92/28" max-width="164">
          <Logo class="svg-width text-neutral900" />
        </v-responsive>
        <h3 class="text-sm-h3 text-h4 text-center gradient-text font-weight-black">
          {{ t('under_maintenance.title') }}
        </h3>
        <p class="release-notification-text text-body-1 text-center mt-3">
          {{ t('under_maintenance.description') }}
        </p>
        <BrandButton
          variant="primary" size="x-large" class="mt-8" :href="updateNewsLink"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t('under_maintenance.cta') }}
        </BrandButton>
        <v-spacer />
      </div>
    </v-overlay>

    <ToastFactory />
    <ModalFactory />

    <RatingPromptDialog v-if="ready && !isExtensionMini" />

    <ExtensionMini v-if="ready && isExtensionMini" />
    <Layout v-if="ready && !isExtensionMini" />
  </v-app>
</template>

<script setup>
import { ref, computed, watch, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
// import { useHead } from '@vueuse/head'
import { SplashScreen } from '@capacitor/splash-screen'

import ToastFactory from '@/components/factories/ToastFactory'
import ModalFactory from '@/components/factories/ModalFactory'
import RatingPromptDialog from '@/components/global/RatingPromptDialog.vue'
import UpdateApplicationOverlay from '@/components/overlays/UpdateApplicationOverlay'
import ExtensionMini from '@/ExtensionMini'
import Layout from '@/components/global/Layout.vue'
import BrandButton from '@/components/global/brand/BrandButton.vue'
import IntentAddingMessage from '@/components/overlays/IntentAddingMessage.vue'
import DisplayWatcher from '@/components/global/DisplayWatcher.vue'
import safeArea from '@/plugins/safe-area-insets'
import toast from '@/plugins/toasts'
import { UPDATE_NEWS_LINKS } from '@/utils/constants'
import { getRouteInheritedMeta } from '@/utils/helpers'
import { useRatingPrompt } from '@/composables/useRatingPrompt'
import { useHead } from '@unhead/vue'

// Set up store, route, and i18n
const store = useStore()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

// The initial navigation can be blocked in the router guard (e.g. the extension
// floating window awaits auth before resolving /extension/options). Until it
// settles, the current route is the unmatched START_LOCATION, which falls back
// to the 'default' layout (AppBar) — so gate rendering on router readiness to
// avoid flashing that layout before the real route mounts.
const routerReady = ref(false)
router.isReady().finally(() => { routerReady.value = true })
const display = useDisplay()

// Computed properties - store getters
const userId = computed(() => store.getters['auth/userId'])
const isOnline = computed(() => store.getters['device/online'])
const isExtensionMini = computed(() => store.getters['device/isExtensionMini'])
const localeLoaded = computed(() => store.getters['device/localeLoaded'])
const isUnderMaintenance = computed(() => store.getters['features/UnderMaintenance'])
const intent = computed(() => store.getters['native/intent'])

// Other computed properties
const ready = computed(() => localeLoaded.value && isOnline.value && routerReady.value)

// Computed styles to set CSS variables dynamically
const styles = computed(() => {
  // Only apply layout left offset on desktop when sidebar should be visible
  // Use inherited route meta to check sidebar visibility
  const meta = getRouteInheritedMeta(route)
  const showSidebar = display.mdAndUp.value &&
                     (meta.sidebarVisibility === true ||
                      (typeof meta.sidebarVisibility === 'function' &&
                       meta.sidebarVisibility({ display })))

  return {
    '--v-layout-left': showSidebar ? '320px !important' : '0px !important',
  }
})

const systemBarProps = computed(() => {
  const parentRoute = route.matched[0]

  return {
    height: safeArea.top,
    ...parentRoute?.meta?.systemBar,
    ...route?.meta?.systemBar,
  }
})

const updateNewsLink = computed(() => {
  return UPDATE_NEWS_LINKS[locale.value]
})

// Methods
function onNetworkRetry() {
  store.dispatch('device/checkDeviceConnectivity').catch(() => {
    toast.error(t('common.NO_NETWORK'))
  })
}

// In-app rating prompt: surfaces (e.g. the add-details page) QUEUE the soft-ask
// rather than show it in place; flush it after the user navigates so it lands on
// the destination page, never on the requesting surface.
const { flushPendingRating } = useRatingPrompt()
watch(() => route.fullPath, () => { flushPendingRating().catch(() => {}) })

// Watchers
watch(ready, async (val) => {
  if (val && store.getters['device/isNative']) {
    await SplashScreen.hide()
  }
}, { immediate: true })

useHead({
  lang: locale,
  title: computed(() => t('page.default.meta.title')),
  titleTemplate: computed(() => (chunk) =>
    t(chunk ? 'page.default.meta.title_template' : 'page.default.meta.title', [chunk])
  ),
  meta: [
    {
      name: 'description',
      content: computed(() => t('page.default.meta.description')),
    },
  ],
})

</script>

<style lang="scss" scoped>
.v-system-bar {
  z-index: 99999;
}

.loader-overlay :deep(.v-overlay__content) {
  height: 100%;
}

.loader-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}

.gradient-text {
  background:
      linear-gradient(
        to right,
        rgb(var(--v-theme-blue400)),
        rgb(var(--v-theme-teal400))
      );
    background-clip: text;
    color: transparent;
}

.release-notification-text {
  max-width: 500px;
}
</style>
